import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from "axios";
import { Wallet } from '../utils/wallet';
import { DepositHistory } from '../utils/depositHistory';
import { AppDispatch, RootState } from "../app/store";
import { WithdrawHistory } from '../utils/withdrawHistory';
import { getBalance } from 'keycloakauthmodule';
import {vpcPriceUrl} from "../api/apiConfig";

// Define the response interface
export interface VpcPrice {
  usdt_vpc: { cost: number; amount: number };
  sol_vpc: { cost: number; amount: number };
  sol_usdt: { cost: number; amount: number };
}

// Define the initial state
interface WalletsState {
  wallet: Wallet | null;
  getDepositWalletStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  getDepositWalletError: string | null;
  isGetDepositWalletInprogress: boolean;
  depositHistory: Array<DepositHistory>;
  getDepositHistoryStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  getDepositHistoryError: string | null;
  isNeedToGoFirstTransactionPage: boolean;
  vpcPrice: VpcPrice | null;
  getVpcPriceStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  getVpcPriceError: string | null;
  withdrawHistory: Array<WithdrawHistory>;
  getWithdrawHistoryStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  getWithdrawHistoryError: string | null;
  requestWithdrawStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  requestWithdrawError: string | null;
  convertBalanceStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  convertBalanceError: string | null;
}

const initialState: WalletsState = {
  wallet: null,
  getDepositWalletStatus: 'idle',
  getDepositWalletError: null,
  isGetDepositWalletInprogress: false,
  depositHistory: [],
  getDepositHistoryStatus: 'idle',
  getDepositHistoryError: null,
  isNeedToGoFirstTransactionPage: false,
  vpcPrice: null,
  getVpcPriceStatus: 'idle',
  getVpcPriceError: null,
  withdrawHistory: [],
  getWithdrawHistoryStatus: 'idle',
  getWithdrawHistoryError: null,
  requestWithdrawStatus: 'idle',
  requestWithdrawError: null,
  convertBalanceStatus: 'idle',
  convertBalanceError: null,
};

// Async action to convert balance
export const convertBalance = createAsyncThunk<
  any,
  { amount: number; from_balance: string; to_balance: string },
  { rejectValue: string; dispatch: AppDispatch }
>(
  'wallets/convertBalance',
  async ({ amount, from_balance, to_balance }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(
        `user/convert-balance`,
        {},
        {
          params: { amount, from_balance, to_balance },
          headers: { accept: 'application/json' },
        }
      );

      // Optionally, refresh the balance after conversion
      await dispatch(getBalance()).unwrap();
      await dispatch(setIsNeedToGoFirstTransactionPageFlag(true));
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Failed to convert balance";

      return rejectWithValue(errorMessage);
    }
  }
);



// Async action to fetch VPC price
export const getVpcPrice = createAsyncThunk<
  VpcPrice,
  void,
  { rejectValue: string }
>(
  'wallets/getVpcPrice',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(vpcPriceUrl, {
        headers: {
          accept: 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch VPC price');
    }
  }
);


// Async action to fetch the deposit wallet
export const getDepositWallet = createAsyncThunk<
  Wallet,
  void,
  { rejectValue: string }
>(
  'wallets/getDepositWallet',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post('crypto/deposit-request');
      const data = response.data;

      if (data.message === "Wallet is already assigned!" && data.wallet) {
        return data.wallet;
      }

      return rejectWithValue(data.message || "Unexpected response");
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to fetch wallet");
    }
  }
);

// Async action to fetch the deposit history
export const getDepositHistory = createAsyncThunk<
  { offset: number; limit: number; count: number; total: number; list: Array<DepositHistory>; },
  { offset: number; limit: number },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  'wallets/getDepositHistory',
  async ({ offset, limit }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await axios.get(`crypto/deposit-history?offset=${offset}&limit=${limit}`);
      const newHistory = response.data.list;

      const walletState = getState().wallet;
      // Compare the current depositHistory with the new one
      const currentHistory = walletState.depositHistory;
      const hasChanged = JSON.stringify(currentHistory) !== JSON.stringify(newHistory);

      if (hasChanged) {
        // Dispatch getBalance if depositHistory has changed
        await dispatch(getBalance()).unwrap();
        // Need to update the user transactions to reflect and also move to page 1
        await dispatch(setIsNeedToGoFirstTransactionPageFlag(true));
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail?.[0]?.msg ||
        error.message ||
        "Failed to fetch deposit history"
      );
    }
  }
);


// Async action to fetch the deposit history
export const getWithdrawHistory = createAsyncThunk<
  { offset: number; limit: number; count: number; total: number; list: Array<WithdrawHistory>; },
  { offset: number; limit: number },
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>(
  'wallets/getWithdrawHistory',
  async ({ offset, limit }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await axios.get(`crypto/withdraw-history?offset=${offset}&limit=${limit}`);
      const newHistory = response.data.list;

      const walletState = getState().wallet;
      // Compare the current depositHistory with the new one
      const currentHistory = walletState.depositHistory;
      const hasChanged = JSON.stringify(currentHistory) !== JSON.stringify(newHistory);

      if (hasChanged) {
        // Dispatch getBalance if depositHistory has changed
        await dispatch(getBalance()).unwrap();
        // Need to update the user transactions to reflect and also move to page 1
        await dispatch(setIsNeedToGoFirstTransactionPageFlag(true));
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail?.[0]?.msg ||
        error.message ||
        "Failed to fetch withdraw history"
      );
    }
  }
);

// Async action to request a withdrawal
export const requestWithdraw = createAsyncThunk<
  any,
  { withdraw_wallet_address: string; amount: number, token_type: string },
  { rejectValue: string, dispatch: AppDispatch; }
>(
  'wallets/requestWithdraw',
  async ({ withdraw_wallet_address, amount, token_type }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(
        `crypto/withdraw-request`,
        {},
        {
          params: { withdraw_wallet_address, amount, token_type },
        }
      );

      await dispatch(getWithdrawHistory({ offset: 0, limit: 20 })).unwrap();

      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||  // Extract 'detail' field
        error.response?.data?.message ||
        error.message ||
        'Failed to request withdrawal';

      return rejectWithValue(errorMessage);
    }
  }
);




const walletSlice = createSlice({
  name: 'wallets',
  initialState,
  reducers: {
    setIsNeedToGoFirstTransactionPageFlag: (state, action: PayloadAction<boolean>) => {
      state.isNeedToGoFirstTransactionPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDepositWallet.pending, (state) => {
        state.getDepositWalletStatus = 'loading';
        state.getDepositWalletError = null;
        state.isGetDepositWalletInprogress = true;
      })
      .addCase(getDepositWallet.fulfilled, (state, action: PayloadAction<Wallet>) => {
        state.getDepositWalletStatus = 'succeeded';
        state.wallet = action.payload;
        state.isGetDepositWalletInprogress = false;
      })
      .addCase(getDepositWallet.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.getDepositWalletStatus = 'failed';
        state.getDepositWalletError = action.payload || 'Failed to fetch wallet';
        state.isGetDepositWalletInprogress = false;
      })
      .addCase(getDepositHistory.pending, (state) => {
        state.getDepositHistoryStatus = 'loading';
        state.getDepositHistoryError = null;
      })
      .addCase(getDepositHistory.fulfilled, (state, action: PayloadAction<{ offset: number; limit: number; count: number; total: number; list: Array<DepositHistory>; }>) => {
        state.getDepositHistoryStatus = 'succeeded';
        state.depositHistory = action.payload.list;
      })
      .addCase(getDepositHistory.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.getDepositHistoryStatus = 'failed';
        state.getDepositHistoryError = action.payload || 'Failed to fetch deposit history';
      })
      .addCase(getWithdrawHistory.pending, (state) => {
        state.getWithdrawHistoryStatus = 'loading';
        state.getWithdrawHistoryError = null;
      })
      .addCase(getWithdrawHistory.fulfilled, (state, action: PayloadAction<{ offset: number; limit: number; count: number; total: number; list: Array<WithdrawHistory>; }>) => {
        state.getWithdrawHistoryStatus = 'succeeded';
        state.withdrawHistory = action.payload.list;
      })
      .addCase(getWithdrawHistory.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.getWithdrawHistoryStatus = 'failed';
        state.getWithdrawHistoryError = action.payload || 'Failed to fetch withdraw history';
      })
      .addCase(getVpcPrice.pending, (state) => {
        state.getVpcPriceStatus = 'loading';
        state.getVpcPriceError = null;
      })
      .addCase(getVpcPrice.fulfilled, (state, action: PayloadAction<VpcPrice>) => {
        state.getVpcPriceStatus = 'succeeded';
        state.vpcPrice = action.payload;
      })
      .addCase(getVpcPrice.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.getVpcPriceStatus = 'failed';
        state.getVpcPriceError = action.payload || 'Failed to fetch VPC price';
      })
      .addCase(requestWithdraw.pending, (state) => {
        state.requestWithdrawStatus = 'loading';
        state.requestWithdrawError = null;
      })
      .addCase(requestWithdraw.fulfilled, (state) => {
        state.requestWithdrawStatus = 'succeeded';
      })
      .addCase(requestWithdraw.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.requestWithdrawStatus = 'failed';
        state.requestWithdrawError = action.payload || 'Failed to request withdrawal';
      })
      .addCase(convertBalance.pending, (state) => {
        state.convertBalanceStatus = 'loading';
        state.convertBalanceError = null;
      })
      .addCase(convertBalance.fulfilled, (state) => {
        state.convertBalanceStatus = 'succeeded';
      })
      .addCase(convertBalance.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.convertBalanceStatus = 'failed';
        state.convertBalanceError = action.payload || 'Failed to convert balance';
      });
  },
});

export const { setIsNeedToGoFirstTransactionPageFlag } = walletSlice.actions;


export default walletSlice.reducer;
